import "./env"; // FOUND-09 — Zod validation fires at module load; throws on invalid/missing env.

// Activates module augmentation of @tanstack/react-router's
// StaticDataRouteOption with `breadcrumb`. Must run before route files are
// loaded so `staticData: { breadcrumb: {...} }` typechecks in every route.
// See CONTEXT §D-09 + RESEARCH Pitfall 3.
import "@linkhr/design-system/patterns/Breadcrumbs/router-augmentation";

import { createDefaultQueryClient } from "@linkhr/api-client";
import { GLOBAL_LOGIN_PATH, configureAuthNetwork, resolveTenant } from "@linkhr/auth";
import { createAppRoot } from "@linkhr/auth/react";
import { AuthSplash } from "@linkhr/auth/pages";
import { initI18n } from "@linkhr/i18n";
import { useBillingWarningStore, ErrorScreen, NotFoundScreen } from "@linkhr/design-system";
import { routeTree } from "./routeTree.gen";
import { env } from "./env";
import "./styles/app.css";

/**
 * Employee Web — app bootstrap (Phase 1 Plan 06, extended by Phase 2 Plan 02-11).
 *
 * Delegates to `createAppRoot` from `@linkhr/auth/react`, which runs the
 * shared 7-step sequence (configureAuthNetwork → MSW → Splash → refreshOnLoad
 * → TokenProvider → createRouter → RouterProvider). Awaiting refreshOnLoad
 * BEFORE <RouterProvider> mounts closes Pitfall H2 (redirect loop on hard
 * refresh) because requireAuth's first read sees the real snapshot.
 *
 * Phase 2 additions (mirror HR Admin Plan 02-10):
 *   - `@linkhr/design-system/patterns/Breadcrumbs/router-augmentation`
 *     side-effect import (above) — activates the TanStack Router
 *     `StaticDataRouteOption` augmentation so every route's
 *     `staticData: { breadcrumb: {...} }` typechecks.
 *   - `setBillingWarning` wired through createAppRoot → ApiProvider so the
 *     api-client billing middleware (Plan 02-01) pipes severity into the
 *     design-system BillingWarningStore (Plan 02-09 pattern; D-20 decoupling).
 *     Employee Web tenants see the same seats/past-due/suspended banners as
 *     HR Admin when the X-LinkHR-Billing-Warning header arrives — role-gating
 *     inside `<BillingBanner>` hides it for non-HR_ADMIN viewers.
 *
 * Audience is `"mobile"` — tenant employees on `{slug}.linkhr.{jo|test}`
 * (same subdomain as hr-admin; aud claim differentiates role surface).
 *
 * PITFALL C2 — MSW must be loaded via dynamic import inside an env guard so
 * bundlers tree-shake it from production. See apps/hr-admin/src/main.tsx for
 * the canonical explanation; this file mirrors the pattern for employee-web.
 */
async function enableMocksIfConfigured(): Promise<void> {
  // Prod-tree-shake gate — literal chain required per project conventions §Vite quirks.
  if (!import.meta.env.DEV) return;

  // Runtime toggle: ?mock=1|0 > sessionStorage > localStorage > VITE_USE_MSW env.
  // Shared helper lives in @linkhr/api-client/mocks so all 3 apps share precedence.
  const { shouldEnableMsw } = await import("@linkhr/api-client/mocks");
  const { enabled, source } = shouldEnableMsw(import.meta.env.VITE_USE_MSW);

  if (!enabled) {
    console.info(`[linkhr] MSW disabled → real backend (source: ${source})`);
    return;
  }

  console.info(`[linkhr] MSW enabled (source: ${source})`);
  const { worker, WORKER_URL } = await import("./mocks/browser");
  await worker.start({
    onUnhandledRequest: "bypass",
    serviceWorker: { url: WORKER_URL },
    // MSW would log every request with its body, a typed password included.
    quiet: true,
  });
}

/**
 * Which company is this? The address answers on a per-company host
 * (`acme.hr.linkco.jo`); the session answers on the global entry host
 * (`app.[staging.]hr.linkco.jo`), which serves this same app for EVERY
 * company. When neither does, `resolveTenant` returns `""` rather than the
 * build-time `VITE_DEV_TENANT_SLUG` — which defaults to the demo company and
 * would quietly sign a stranger into it. The honest next step is to ask, so
 * hand the visitor to the global login before anything boots, fetches or
 * renders. `replace` so Back does not bounce them straight back here.
 *
 * Public pages are the exception: the letter-verification page a printed QR
 * points at is opened by strangers on phones with no session at all, and its
 * token alone resolves the letter — no company needed.
 */
const PUBLIC_PATH_PREFIXES = ["verify/"];

function isPublicPath(): boolean {
  const path = window.location.pathname.slice(import.meta.env.BASE_URL.length);
  return PUBLIC_PATH_PREFIXES.some((prefix) => path.startsWith(prefix));
}

function hasTenantOrLeave(): boolean {
  if (isPublicPath()) return true;
  if (resolveTenant({ fallbackSlug: env.VITE_DEV_TENANT_SLUG }) !== "") return true;
  window.location.replace(GLOBAL_LOGIN_PATH);
  return false;
}

async function boot(): Promise<void> {
  // createAppRoot repeats this; it has to run FIRST so the tenant guard
  // below reads this app's own audience-scoped session slug rather than
  // the module default.
  configureAuthNetwork({ baseUrl: env.VITE_API_BASE_URL, aud: "mobile" });
  if (!hasTenantOrLeave()) return;
  await initI18n({
    backendLoadPath: `${import.meta.env.BASE_URL}locales/{{lng}}/{{ns}}.json`,
  });
  // createDefaultQueryClient wires the shared meta.invalidates handler so
  // mutations declaring `meta: { invalidates: [...] }` actually refetch the
  // listed query prefixes on success.
  const queryClient = createDefaultQueryClient();
  await createAppRoot({
    aud: "mobile",
    routeTree,
    apiBaseUrl: env.VITE_API_BASE_URL,
    SplashComponent: AuthSplash,
    queryClient,
    enableMocks: enableMocksIfConfigured,
    defaultRoute: "/",
    basePath: "/me",
    setBillingWarning: (severity) => useBillingWarningStore.getState().setBillingWarning(severity),
    DefaultNotFoundComponent: () => <NotFoundScreen homeHref={import.meta.env.BASE_URL} />,
    DefaultErrorComponent: ErrorScreen,
  });
}

void boot();
